import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Params } from '@angular/router';
import { Environment } from '@core/services/common';
import { AnalyticsContext } from '@core/services/core/analytics/analytics-context';
import { ContextService } from '@core/services/core/context.service';
import { ExternalUrlOpenerHelperService } from '@core/services/core/external-url-opener-helper.service';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { NavigationHelper } from '@core/services/core/navigation-helper.service';
import { OperationRegisterService } from '@core/services/core/operation-register.service';
import { TecuidamosService } from '@core/services/core/tecuidamos.service';
import { UiHelperService } from '@core/services/core/ui-helper.service';
import { AddressHelperService } from '@core/services/helpers/address-helper.service';
import { ResultRegisterHelperService } from '@core/services/helpers/result-register-helper.service';
import { UrlHelper } from '@core/services/helpers/url-helper.service';
import { Device } from '@ionic-native/device/ngx';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { Platform } from '@ionic/angular';
import { Province } from '@models/address';
import { UserStatus } from '@models/authorization';
import { RegistersRO, RegistersStructure } from '@models/operation-register';
import { LoginRouting } from '@modules/login/models';
import { PHONE_NUMBERS } from '@modules/receipt-payment/receipt-payment-constants';
import { METS_PREFIX_REGISTER_PAGE_TITLE, RegisterAnalyticsPageTitles } from '@modules/register/analytics/register.analytics-constants';
import { getRegisterCommonLiterals } from '@modules/register/analytics/register.analytics-inputs';
import { ErrorSendSMSModalComponent } from '@modules/register/components/error-send-sms-modal/error-send-sms-modal-component';
import {
  InitialDataValidatonResponseCodes as VALIDATION_DATA_CODES,
  RegisterAttempts,
  RegisterErrors,
  RegisterLoadingId,
  RegisterMatchCode,
  RegisterQueryParams,
  RegisterResponse,
  RegisterRouting,
  RegisterState,
  RegisterUserHttpResponse,
  RegisterUserResponseCodes,
  RegisterValidatePinEmailResponse,
  RegisterValidatePinSMSResponse,
  ResultRegisterOptions,
  ValidateEmailResponse,
  ValidateOnlineCampaignUserResponseCodes
} from '@modules/register/models/register';
import { RegisterService } from '@modules/register/services/register.service';
import { ResultCustomButtons, ResultCustomHeader } from '@modules/result-custom/result-custom.page';
import { TranslateService } from '@ngx-translate/core';
import { spinner } from '@utils/rxjs';
import { StoreUtils } from '@utils/store';
import { getDocumentNumberType, getUserAgent, hasSpecialCharacters, isOlderThan } from '@utils/utils';
import * as moment from 'moment';
import { BehaviorSubject, forkJoin } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RegisterHelperService extends StoreUtils<RegisterState> {

  isSupportFormActive: boolean;
  loginFn: (state: Partial<RegisterState>) => void;

  private _attemptsRegister: number;
  private incorrectLoginTemporal = new BehaviorSubject<boolean>(false);

  constructor(
    private url: UrlHelper,
    private translate: TranslateService,
    private ui: UiHelperService,
    private ctx: ContextService,
    private or: OperationRegisterService,
    private addressHelper: AddressHelperService,
    private resultRegisterHelper: ResultRegisterHelperService,
    private nav: NavigationHelper,
    private mmobileHelper: MMobileHelperService,
    private registerService: RegisterService,
    private iab: InAppBrowser,
    private analyticsContext: AnalyticsContext,
    private urlExternal: ExternalUrlOpenerHelperService,
    private platform: Platform,
    private device: Device,
    private teCuidamosService: TecuidamosService

  ) {
    super();
    this.isSupportFormActive = this.mmobileHelper.checkIsFeatureEnabled(
      this.ctx.isApp ? 'supportForm_APP' : 'supportForm_WEB'
    );
  }

  get attemptsRegister(): number {
    return this._attemptsRegister;
  }

  set attemptsRegister(value: number) {
    this._attemptsRegister = value;
  }

  getIncorrectLoginTemporal() {
    return this.incorrectLoginTemporal.asObservable();
  }

  setIncorrectLoginTemporal(value) {
    this.incorrectLoginTemporal.next(value);
  }

  isOnlineCampaignUser(status: number): boolean {
    return Number(status) === UserStatus.ONLINE_CAMPAIGN;
  }

  cleanUnnecessaryWhiteSpaces(form: AbstractControl): void{
    form?.setValue(form?.value.trimEnd());
  }

  registerROClientNoProfile(state: Partial<RegisterState>) {
    const structure: any = this.getDeviceStructure(RegistersStructure.CLIENT_NO_PROFILE, state);
    structure.birthdate = state.birthdate;
    structure.hasHealthPolicies = state.hasHealthPolicies;
    structure.errorMessage = 'Edad no permitida';
    this.or.customerSignUpRO(
      RegistersRO.CLIENT_NO_PROFILE,
      JSON.stringify(structure),
      state
    );
  }

  async queryParamsHandler(params: Params, state: Partial<RegisterState>): Promise<Partial<RegisterState>> {
    state.queryParamsChecked = true;
    const actions = {
      [RegisterQueryParams.HASH]: async (key: string) => {
        const data = await this.registerService.decryptHash(key);
        state.userHash = true;
        state.hashTC = key;
        const userData = this.mapUserDataHashTC(data, state.documentNumber);
        state = { ...state, ...userData };
      },
      [RegisterQueryParams.SRV]: (srv: string) => {
        const structure = JSON.stringify(this.getDeviceStructure(RegistersStructure.TE_CUIDAMOS, state));
        this.or.customerSignUpRO(
          RegistersRO.TE_CUIDAMOS,
          structure,
          state
        );
        state = { ...state, srv };
      },
      [RegisterQueryParams.CHANNEL]: (channel: string) => {
        state = { ...state, channel };
      },
      [RegisterQueryParams.SUBCHANNEL_LOWER]: (subchannel: string) => {
        state = { ...state, subchannel };
      },
      [RegisterQueryParams.SUBCHANNEL]: (subchannel: string) => {
        state = { ...state, subchannel };
      }
    };

    const keys = Object.keys(params);

    if (keys.length) {
      for (const key of keys) {
        const action = actions[key];
        if (typeof action === 'function') await action(params[key]);
      }
    }

    this.updateState(state);

    return state;
  }

  openUrl(url: string): void {
    this.url.open(url);
  }

  navTo(path: string, queryParams = {}): void {
    this.nav.toNew(RegisterRouting.BASE_PATH + path, { queryParams });
  }

  navToAttemptsExceeded(): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.SECURITY_CODE_SMS_ATTEMPTS_EXCEEDED.TITLE',
        message: 'REGISTER.ERRORS.SECURITY_CODE_SMS_ATTEMPTS_EXCEEDED.MESSAGE',
        infoCall: false
      },
      buttons: {
        primary: {
          type: 'primary',
          text: 'BACK_HOME',
          onClick: () => this.goToPortalHome()
        }
      }
    };
    this.resultRegisterHelper.options = options;
    this.navToResultRegister();
  }

  navToCanNotRegisterMTC(registerAction: Function): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.CANNOT_REGISTER_MTC.TITLE',
        message: 'REGISTER.ERRORS.CANNOT_REGISTER_MTC.MESSAGE',
        infoCall: false
      },
      buttons: {
        primary: {
          type: 'primary',
          text: 'REGISTER.ERRORS.CANNOT_REGISTER_MTC.BUTTON',
          onClick: () => registerAction()
        }
      }
    };
    this.resultRegisterHelper.options = options;
    this.navToResultRegister();
  }

  navToError(code?, message?): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.GENERIC.TITLE',
        message: 'REGISTER.ERRORS.GENERIC.MESSAGE',
        infoCall: true
      },
      buttons: {
        primary: {
          type: 'primary',
          text: 'BACK_HOME',
          onClick: () => this.goToPortalHome()
        }
      }
    };
    this.resultRegisterHelper.options = options;
    this.resultRegisterHelper.analytics = this.getDefaultAnalyticsErrorParams(RegisterAnalyticsPageTitles.SERVER_ERROR, code, message);
    this.navToResultRegister();
  }

  navToCustomerNonTakerError(code: string, message: string): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.CUSTOMER_NON_TAKER.TITLE',
        message: 'REGISTER.ERRORS.CUSTOMER_NON_TAKER.MESSAGE',
        infoCall: false
      },
      buttons: {
        primary: {
          type: 'primary',
          text: 'BACK_HOME',
          onClick: () => this.goToPortalHome()
        }
      }
    };
    this.resultRegisterHelper.options = options;
    this.resultRegisterHelper.analytics = this.getDefaultAnalyticsErrorParams(RegisterAnalyticsPageTitles.NON_TAKER_POLICY, code, message);
    this.navToResultRegister();
  }

  navToNotIsOlder(birthdate: string): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.NOT_IS_OLDER.TITLE',
        message: 'REGISTER.ERRORS.NOT_IS_OLDER.MESSAGE',
        infoCall: false,
        inputs: [{
          value: moment(birthdate).format('DD/MM/YYYY'),
          button: {
            type: 'link',
            title: 'REGISTER.ERRORS.NOT_IS_OLDER.BUTTON',
            onClick: () => this.nav.pop()
          }
        }]
      },
      buttons: {
        primary: {
          type: 'primary',
          text: 'BACK_HOME',
          onClick: () => this.goToPortalHome()
        }
      }
    };
    this.resultRegisterHelper.options = options;
    this.navToResultRegister();
  }

  navToCustomerCanNotConsulted(): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.CUSTOMER_CANNOT_CONSULTED.TITLE',
        message: 'REGISTER.ERRORS.CUSTOMER_CANNOT_CONSULTED.MESSAGE',
        infoCall: false
      },
      buttons: {
        primary: {
          type: 'primary',
          text: 'BACK_HOME',
          onClick: () => this.goToPortalHome()
        }
      }
    };
    this.resultRegisterHelper.options = options;
    this.navToResultRegister();
  }

  navToIban(iban: boolean, phone: string, email: string): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.MORE_DATA.TITLE',
        message: 'REGISTER.ERRORS.MORE_DATA.MESSAGE',
        infoCall: true,
        infoCallMessage: 'REGISTER.VALIDATE_IBAN.INFO_CALL_MSG',
        inputs: [
          {
            label: 'REGISTER.VALIDATE_CONTACT_DATA.PLACEHOLDERS.MAIL',
            value: email,
            button: {
              type: 'link',
              title: 'REGISTER.ERRORS.MORE_DATA.BUTTON',
              onClick: () => this.navTo(RegisterRouting.VALIDATE_EMAIL, { secondAuth: true, iban })
            }
          },
          {
            label: 'REGISTER.VALIDATE_CONTACT_DATA.PLACEHOLDERS.NUMBER_PHONE',
            value: phone,
            button: {
              type: 'link',
              title: 'REGISTER.ERRORS.MORE_DATA.BUTTON',
              onClick: () => this.navTo(RegisterRouting.VALIDATE_PHONE, { secondAuth: true, iban })
            }
          }
        ],
        infoDesktop: true
      }
    };
    this.resultRegisterHelper.options = options;
    this.navToResultRegister();
  }

  navToRegisterSuccess(state: Partial<RegisterState>, title: string, message: string, buttonTitle: string, loginFn: Function, errMtC = false): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title,
        message,
        infoCall: false,
        imgDesktop: 'assets/images/register-success-desktop.png',
        imgMobile: 'assets/images/register-success-mobile.png',
      },
      buttons: {
        primary: {
          text: buttonTitle,
          type: 'primary',
          onClick: () => loginFn(state)
        },
        reverse: true
      }
    };
    if (this.ctx?.isDesktop) {
      options.buttons.buttonsDownloadApp = true;
    }
    if (errMtC) {
      options.data.errMtC = 'REGISTER.RESULT_SUCCESS_REGISTER.ERROR_MTC';
      const titleAnalytics = this.getPageTitle(RegisterAnalyticsPageTitles.REGISTER_SUCCESS_TECUIDAMOS_ERROR);
      this.resultRegisterHelper.analytics = {
        Pagetitle: titleAnalytics,
        CD133_process_step_number: 'step-8-' + titleAnalytics
      };
    } else {
      const titleAnalytics = state.MtC
        ? `${RegisterAnalyticsPageTitles.REGISTER_SUCCESS_TECUIDAMOS}`
        : `${RegisterAnalyticsPageTitles.REGISTER_SUCCESS}`;
      this.resultRegisterHelper.analytics = {
        Pagetitle:  `${METS_PREFIX_REGISTER_PAGE_TITLE}${titleAnalytics}`,
        CD31_section3: titleAnalytics.substring(1),
        CD133_process_step_number: 'step-8-' + titleAnalytics.substring(1)
      };
    }

    this.resultRegisterHelper.options = options;
    this.navToResultRegister();
  }

  navToUserOnboardingError(): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.USER_ONBOARDING.TITTLE',
        message: 'REGISTER.ERRORS.USER_ONBOARDING.MESSAGE',
        infoCall: false
      },
      buttons: {
        primary: {
          type: 'primary',
          text: 'BACK_HOME',
          onClick: () => this.goToPortalHome()
        }
      }
    };

    this.resultRegisterHelper.options = options;
    this.navToResultRegister();
  }


  navToIncomingCallError(): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.COSTUMER_NO_PROFILE.TITLE',
        message: 'REGISTER.ERRORS.INCOMING_CALL.TITLE',
        infoCall: true
      },
      buttons: {
        primary: {
          text: 'REGISTER.ERRORS.CHANGE_USER_STATUS.PRIMARY_BTN',
          type: 'primary',
          onClick: () => this.nav.cancelProcess()
        }
      }
    };
    this.resultRegisterHelper.options = options;
    this.navToResultRegister();
  }

  navToAlreadyRegister(name: string, code: string, message: string): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        titleHi: this.translate.instant('REGISTER.ERRORS.ALREADY_REGISTER.TITLE_HI', { name }),
        title: this.translate.instant('REGISTER.ERRORS.ALREADY_REGISTER.TITLE', { name }),
        message: 'REGISTER.ERRORS.ALREADY_REGISTER.MESSAGE',
        infoCall: false
      },
      buttons: {
        primary: {
          text: 'REGISTER.ERRORS.ALREADY_REGISTER.PRIMARY_BTN',
          type: 'primary',
          onClick: () => this.nav.goLoginPage(true)
        },
        secondary: {
          text: 'REGISTER.ERRORS.ALREADY_REGISTER.LINK_BTN',
          type: 'link',
          onClick: () => this.nav.toNew(['login', LoginRouting.FORGOT_PASSWORD, this.ctx.isDesktop ? LoginRouting.MAIL : LoginRouting.PHONE])
        }
      }
    };
    this.resultRegisterHelper.options = options;
    this.resultRegisterHelper.analytics = this.getDefaultAnalyticsErrorParams(RegisterAnalyticsPageTitles.ALREADY_REGISTER, code, message);
    this.navToResultRegister();
  }

  navToDocumentInvalid(): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.DOCUMENT_INVALID.TITLE',
        message: 'REGISTER.ERRORS.DOCUMENT_INVALID.MESSAGE',
        infoCall: false
      },
      buttons: {
        primary: {
          type: 'primary',
          text: 'BACK_HOME',
          onClick: () => this.goToPortalHome()
        }
      }
    };
    this.resultRegisterHelper.options = options;
    this.navToResultRegister();
  }

  navToClientDuplicateNotMatch(code: string, message: string): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.CLIENT_DUPLICATE_NOT_MATCH.TITLE',
        message: 'REGISTER.ERRORS.CLIENT_DUPLICATE_NOT_MATCH.MESSAGE',
        infoCall: true
      },
      buttons: {
        primary: {
          type: 'primary',
          text: 'BACK_HOME',
          onClick: () => this.goToPortalHome()
        }
      }
    };
    this.resultRegisterHelper.options = options;
    this.resultRegisterHelper.analytics = this.getDefaultAnalyticsErrorParams(RegisterAnalyticsPageTitles.ALREADY_REGISTER, code, message);
    this.navToResultRegister();
  }

  navToNotFoundData(): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.NOT_FOUND_DATA.TITLE',
        message: 'REGISTER.ERRORS.NOT_FOUND_DATA.MESSAGE',
        infoCall: true
      },
      buttons: {
        primary: {
          type: 'primary',
          text: 'BACK_HOME',
          onClick: () => this.goToPortalHome()
        }
      }
    };
    this.resultRegisterHelper.options = options;
    this.navToResultRegister();
  }

  navToCustomerNoProfileError(paramsUrl = false): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.COSTUMER_NO_PROFILE.TITLE',
        message: 'REGISTER.ERRORS.COSTUMER_NO_PROFILE.P1',
        infoCall: true
      },
      buttons: {
        primary: {
          type: 'primary',
          text: 'REGISTER.ERRORS.COSTUMER_NO_PROFILE.SECONDARY_BTN',
          onClick: () => this.nav.cancelProcess()
        }
      }
    };
    this.resultRegisterHelper.options = options;
    this.navToResultRegister(paramsUrl);
  }

  navSendSMSPinError(forwardedPinBySMS: () => void): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.SEND_SMS_PIN.TITLE',
        message: 'REGISTER.ERRORS.SEND_SMS_PIN.MESSAGE',
        infoCall: false
      },
      buttons: {
        primary: {
          type: 'link',
          text: 'TRY_AGAIN',
          onClick: () => forwardedPinBySMS()
        }
      }
    };
    this.resultRegisterHelper.options = options;
    this.navToResultRegister();
  }

  navSendEmailPinError(): void {
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.ERRORS.SEND_EMAIL_PIN.TITLE',
        message: 'REGISTER.ERRORS.SEND_EMAIL_PIN.MESSAGE',
        infoCall: false
      },
      buttons: {
        primary: {
          type: 'link',
          text: 'TRY_AGAIN',
          onClick: () => this.nav.cancelProcess()
        }
      }
    };
    this.resultRegisterHelper.options = options;
    this.navToResultRegister();
  }

  openNotRecieveOTPCodeModal(state: Partial<RegisterState>): void {
    this.ui.presentCustomizableAlert({
      html: `
      <div class="mapfre-fs-m mapfre-fw-500 dark">
    ${this.translate.instant('REGISTER.REQUEST_CODE_MODAL.TEXT')} ${state?.phone.slice(-3)}
      </div>
    `,
      confirmButton: {
        show: true,
        text: this.translate.instant(`CANCEL`),
        cssClass: 'mapfre-fs-s primary-btn',
      },
      cancelButton: {
        show: true,
        text: this.translate.instant('REGISTER.REQUEST_CODE_MODAL.PRIMARY_BTN'),
        cssClass: 'mapfre-fs-s secondary-btn',
        onCancel: () => this.sendPinCodeBySMS(state)

      }
    });
  }

  initialDataValidationResponseHandler(
    response: RegisterResponse,
    state: Partial<RegisterState>,
    formGroup: FormGroup,
    validate: string,
    validateIban = false): () => void {
    const data = this.mapUserData(response);
    let { status, code, message } = response;
    const defaultAction = () => this.navToError();
    if (state.clientDuplicateAuth && code !== VALIDATION_DATA_CODES.OK) {
      return () => this.navToClientDuplicateNotMatch(code, message);
    }
    const actions = {
      200: this.actionsInitialDataResponseHandler200(data, state, response, validateIban, validate, code, message),
      400: this.actionsInitialDataResponseHandler400(formGroup),
      500: () => this.navToError()
    };

    const action = actions[status];

    return !action
      ? defaultAction
      : (typeof action === 'function')
        ? action
        : action[code]
          ? action[code]
          : defaultAction;
  }

  actionsInitialDataResponseHandler200(
    data: Partial<RegisterState>,
    state: Partial<RegisterState>,
    response: RegisterResponse,
    validateIban: boolean,
    validate: string,
    code: string,
    message: string
  ) {
    return {
      [VALIDATION_DATA_CODES.OK]: async () => this.actionsInitialDataOkResponseHandler(data, state, response, validateIban, validate),
      [VALIDATION_DATA_CODES.NO_CLIENT_PROFILE]: () => this.actionsInitialDataNoClientProfileResponseHandler(response, code, message),
      [VALIDATION_DATA_CODES.CUSTOMER_ALREADY_REGISTERED]: () => this.navToAlreadyRegister(response?.name, code, message),
      [VALIDATION_DATA_CODES.CLIENT_DUPLICATE]: () => this.navTo(RegisterRouting.CLIENT_DUPLICATE_DATA),
      [VALIDATION_DATA_CODES.CLIENT_NOT_FOUND]: () => this.navToClientDuplicateNotMatch(code, message),
      [VALIDATION_DATA_CODES.MORE_NECESSARY_DATA]: () => this.actionsInitialDataMoreNecessaryDataResponseHandler(state, validateIban, response),
      [VALIDATION_DATA_CODES.INCOMING_CALL1]: () => this.actionsInitialDataIncomingCall2ResponseHandler(validateIban, state, response),
      [VALIDATION_DATA_CODES.INCOMING_CALL2]: () => this.actionsInitialDataIncomingCall2ResponseHandler(validateIban, state, response),
    };
  }

  actionsInitialDataOkResponseHandler(
    data: Partial<RegisterState>,
    state: Partial<RegisterState>,
    response: RegisterResponse,
    validateIban: boolean,
    validate: string
  ) {
    const stateAux = { ...data, ...state };
    this.updateState(stateAux);
    if (!response?.fechaNacimientoRic?.length && response?.name?.length) {
      this.navTo(RegisterRouting.BIRTHDATE);
      return;
    }

    if (validateIban) {
      state?.initAuth === 'PHONE' ? this.sendPinCodeBySMS(stateAux) : this.sendPinCodeByEmail(stateAux);
      return;
    }

    this.validateResponseHandler(validateIban, validate, response, stateAux);
  }

  actionsInitialDataNoClientProfileResponseHandler(response: RegisterResponse, code: string, message: string) {
    if (response?.isPolicyHolder === 'N'
    || (response?.onlyOfflineProducts === 'N' && response?.fechaNacimientoRic?.length && response?.name?.length) ) {
      this.navToCustomerNonTakerError(code, message);
      return;
    }

    if (response?.onlyOfflineProducts === 'S'
      && response?.fechaNacimientoRic?.length && response?.name?.length) {
      this.navToCustomerCanNotConsulted();
      return;
    }

    if (!response?.fechaNacimientoRic?.length && response?.name?.length) {
      this.navTo(RegisterRouting.BIRTHDATE);
      return;
    }

    this.isNameResponseHandler(response);
  }

  actionsInitialDataIncomingCall2ResponseHandler(
    validateIban: boolean,
    state: Partial<RegisterState>,
    response: RegisterResponse
  ) {
    if (!state?.secondAuth && !validateIban) {
      this.navTo(
        state?.initAuth === 'EMAIL' ? RegisterRouting.VALIDATE_PHONE : RegisterRouting.VALIDATE_EMAIL,
        { secondAuth: true }
      );
      return;
    }

    if (validateIban && state?.secondAuth) {
      this.attemptsRegister = this.attemptsRegister - 1;
      if (!this.attemptsRegister) {
        this.navToNotFoundData();
        return;
      }
      this.navTo(RegisterRouting.VALIDATE_IBAN);
      return;
    }

    if (this.attemptsRegister > 1) {
      this.validateMoreNecessaryDataResponseHandler(response, state, validateIban);
    } else {
      this.navToIncomingCallError();
    }
  }

  actionsInitialDataIncomingCall1ResponseHandler(
    validateIban: boolean,
    state: Partial<RegisterState>
  ) {
    if (validateIban && state?.secondAuth) {
      this.attemptsRegister = this.attemptsRegister - 1;
    }
    this.navToNotFoundData();
  }

  actionsInitialDataMoreNecessaryDataResponseHandler(
    state: Partial<RegisterState>,
    validateIban: boolean,
    response: RegisterResponse
  ) {
    if (!state?.secondAuth && !validateIban) {
      this.navTo(
        state?.initAuth === 'EMAIL' ? RegisterRouting.VALIDATE_PHONE : RegisterRouting.VALIDATE_EMAIL,
        { secondAuth: true }
      );
      return;
    }

    if (validateIban && state?.secondAuth) {
      this.attemptsRegister = this.attemptsRegister - 1;
      if (!this.attemptsRegister) {
        this.navToNotFoundData();
        return;
      }
      this.navTo(RegisterRouting.VALIDATE_IBAN);
      return;
    }

    this.validateMoreNecessaryDataResponseHandler(response, state, validateIban);
  }

  actionsInitialDataResponseHandler400(formGroup: FormGroup) {
    return {
      [VALIDATION_DATA_CODES.WRONG_PHONE_FORMAT]: () => {
        setTimeout(() => {
          const error = formGroup.get('phone') ? { 'validPhone': true } : { validIBAN: true };
          const control = formGroup.get('phone') ? 'phone' : 'IBAN';
          formGroup.get(control).setErrors(error);
          formGroup.get(control).markAsTouched();
        }, 0);
      },
      [VALIDATION_DATA_CODES.WRONG_PAYLOAD1]: () => this.navToError()
    };
  }

  checkBirthdate(hasHealthPolicies: 'N' | 'S', date: string): boolean {
    return hasHealthPolicies === 'N' && isOlderThan(date, 18)
      || (hasHealthPolicies === 'S' && isOlderThan(date, 16));
  }

  validateResponseHandler(validateIban: boolean, validate: string, response: RegisterResponse, stateAux) {
    if (!validateIban && response?.fechaNacimientoRic?.length) {
      validate === 'PHONE' ? this.sendPinCodeBySMS(stateAux) : this.sendPinCodeByEmail(stateAux);
    }
  }

  isNameResponseHandler(response: RegisterResponse) {
    if (!response?.name?.length && !response?.fechaNacimientoRic?.length) {
      this.navToDocumentInvalid();
    }
  }

  validateMoreNecessaryDataResponseHandler(response: RegisterResponse, state: Partial<RegisterState>, validateIban: boolean) {
    if (response?.iban === 'S' && state?.secondAuth && !validateIban) {
      this.navTo(RegisterRouting.VALIDATE_IBAN);
      return;
    }
    if (state?.secondAuth && !validateIban) {
      this.attemptsRegister = 0;
      this.navToIban(false, state?.phone, state?.email);
    }
  }

  getCancelProcessInfoOR(state: Partial<RegisterState>) {
    let documentType = null;
    if (state.documentType) documentType = state.documentType.toString() === '0' ? 'NIF' : 'NIE';
    return `El usuario canceló el proceso proceso. Tipo documento, ${documentType || null} Número de documento,
      ${state?.documentNumber || null} Nombre, ${state?.name || null} Apellido 1, ${state?.lastname1 || null} Apellido 2,
      ${state?.lastname2 || null} Email, ${state?.email || null} Teléfono, ${state?.phone || null} Detalle datos recuperados`;
  }


  registerUserResponseHandler(
    response: RegisterResponse,
    state: Partial<RegisterState>,
    loginFn: Function): () => void {
    const { status, code, message } = response;
    const defaultAction = () => this.navToError();
    const actions = {
      200: {
        [RegisterUserResponseCodes.OK]: () => {
          (!state.MtC)
            ? this.notTeCuidamosResponseHandler(state, response, loginFn)
            : this.teCuidamosResponseHandler(state, response, loginFn);

        },
        [RegisterUserResponseCodes.ONLINE_REGISTRATION_MADE]: () => { this.navToSuccessRegisterUserMTCError(state, loginFn); }
      },
      400: {
        [RegisterUserResponseCodes.WRONG_PAYLOAD1]: () => this.navToError(code, message),
        [RegisterUserResponseCodes.WRONG_PAYLOAD2]: () => this.navToError(code, message)
      },
      406: {
        [RegisterUserResponseCodes.BIRTHDATE_NOT_ACCEPTABLE]: () => this.navToCustomerNoProfileError()
      },
      500: {
        [RegisterUserResponseCodes.CUSTOMER_ALREADY_REGISTERED]: () => this.navToAlreadyRegister(state?.name, code, message),
        [RegisterUserResponseCodes.CHANGE_USER_STATUS_ERROR]: () => this.navToChangeUserStatusError(state?.email || state?.emailRes, code, message),
        [RegisterUserResponseCodes.REGISTER_ERROR1]: () => this.navToRegisterError(state, code, message),
        [RegisterUserResponseCodes.REGISTER_ERROR2]: () => this.navToRegisterError(state, code, message),
        [RegisterUserResponseCodes.REGISTER_ERROR3]: () => this.navToRegisterError(state, code, message),
        [RegisterUserResponseCodes.REGISTER_ERROR4]: () => this.navToRegisterError(state, code, message),
        [RegisterUserResponseCodes.REGISTER_ERROR5]: () => this.navToRegisterError(state, code, message),
        [RegisterUserResponseCodes.CUSTOMER_CANCELLED]: () => this.navToUserCancelledError(code, message),
      }
    };

    const action = actions[status];

    return !action
      ? defaultAction
      : (typeof action === 'function')
        ? action
        : action[code]
          ? action[code]
          : defaultAction;
  }

  teCuidamosResponseHandler(state: Partial<RegisterState>, response: RegisterResponse, loginFn: Function) {
    let title = 'REGISTER.RESULT_SUCCESS_REGISTER.TITLE_GENERIC';
    let msg = 'REGISTER.RESULT_SUCCESS_REGISTER.MESSAGE_GENERIC';
    let btnTitle = state.srv
      ? 'REGISTER.RESULT_SUCCESS_REGISTER.BUTTON_PRIMARY_GENERIC'
      : 'REGISTER.RESULT_SUCCESS_REGISTER.BUTTON_PRIMARY_NO_MTC';

    const errMtC = response.teCuidamos === 'N';
    if (errMtC) {
      title = 'REGISTER.RESULT_SUCCESS_REGISTER.TITLE_NO_MTC';
      msg = 'REGISTER.RESULT_SUCCESS_REGISTER.MESSAGE_NO_MTC';
      btnTitle = 'REGISTER.RESULT_SUCCESS_REGISTER.BUTTON_PRIMARY_NO_MTC';
    }
    this.navToRegisterSuccess(state, title, msg, btnTitle, loginFn, errMtC);
  }

  notTeCuidamosResponseHandler(state: Partial<RegisterState>, response: RegisterResponse, loginFn: Function) {


    const title = 'REGISTER.RESULT_SUCCESS_REGISTER.TITLE_NO_MTC';
    const msg = 'REGISTER.RESULT_SUCCESS_REGISTER.MESSAGE_NO_MTC';
    const btnTitle = 'REGISTER.RESULT_SUCCESS_REGISTER.BUTTON_PRIMARY_NO_MTC';
    this.navToRegisterSuccess(state, title, msg, btnTitle, loginFn);

  }

  securityCodeValidationBySMSResponseHandler(
    response: RegisterValidatePinSMSResponse,
    state: Partial<RegisterState>,
    attempts: RegisterAttempts,
    notMatchPin: RegisterMatchCode): () => void {
    const { status } = response;
    const defaultAction = () => {
      this.defaultActionCodeValidation(state, response);
    };
    const actions = {
      200: () => {
        const s = {
          ...state,
          businessId: response.businessId,
          pinToken: response.token
        };
        this.updateState(s);

        this.registerService.getToken(s).subscribe(token => {
          s.token = token;
          this.updateState(s);
        }, () => this.navToError());
        const structure = JSON.stringify(this.getDeviceStructure(RegistersStructure.IDENTIFIED, state));
        this.or.customerSignUpRO(
          RegistersRO.IDENTIFIED,
          structure,
          state);
        this.navTo(RegisterRouting.CREATE_PASSWORD);
      },
      401: () => {
        notMatchPin.value = true;
        attempts.value = attempts.value - 1;
      },
      403: () => {
        const structure = JSON.stringify(this.getDeviceStructure(RegistersStructure.NO_IDENTIFIED, state, {error: response, message: 'Superado numero de intentos al validar OTP'} as HttpErrorResponse));
        this.or.customerSignUpRO(
          RegistersRO.NO_IDENTIFIED,
          structure,
          state);
        this.navToAttemptsExceeded();
      },
    };

    return actions[status] || defaultAction;
  }

  securityCodeValidationByEmailResponseHandler(
    response: RegisterValidatePinEmailResponse,
    state: Partial<RegisterState>,
    attempts: RegisterAttempts,
    notMatchPin: RegisterMatchCode,
    isRegister = false,
    loginFn?: () => void
    ): () => void {
    const { status } = response;
    const defaultAction = () => {
      this.defaultActionCodeValidation(state, response);
    };
    const actions = {
      200: () => {
        const s = {
          ...state,
          token: response.token,
          checkEmail: true
        };
        this.updateState(s);
        if (isRegister) {
          this.sendRegisterRequest(state, loginFn);
          return;
        }

        this.or.customerSignUpRO(
          RegistersRO.IDENTIFIED,
          RegistersStructure.IDENTIFIED,
          state);
        this.navTo(RegisterRouting.CREATE_PASSWORD);
      },
      401: () => {
        notMatchPin.value = true;
        attempts.value = attempts.value - 1;
      }
    };

    return actions[status] || defaultAction;
  }

  defaultActionCodeValidation(state, response) {
    this.navToError();
    const structure = JSON.stringify(this.getDeviceStructure(RegistersStructure.NO_IDENTIFIED, state, {error: response, message: 'Error en la llamada de validacion OTP'} as HttpErrorResponse));
    this.or.customerSignUpRO(
      RegistersRO.NO_IDENTIFIED,
      structure,
      state);
  }

  validateEmailResponseHandler(
    response: ValidateEmailResponse,
    form: FormGroup,
    navTo = true,
    passwordDataForm = true): () => void {
    const { status, validEmail, } = response;
    const defaultAction = () => this.setErrorInEmailInputs(form, passwordDataForm);
    let action: () => void;

    if (status !== 200) {
      action = () => { this.navToError(); };
      return action;
    }

    if (!validEmail) {
      action = () => { this.setErrorInEmailInputs(form, passwordDataForm); };
      return action;
    }

    action = navTo
      ? () => { this.navTo(RegisterRouting.RGPDUE); }
      : () => { };

    return action || defaultAction;
  }

  loadContacData(state: Partial<RegisterState>): Promise<boolean> {
    return new Promise((resolve, reject) => {
      forkJoin([
        this.addressHelper.getStreetTypes(),
        this.addressHelper.getProvincesList(),
      ]).pipe(spinner(RegisterLoadingId.LOAD_CONTACT_DATA))
        .subscribe({
          next: res => {
            this.updateState({ ...state, streetTypes: res[0], provinces: res[1] });
            resolve(true);
          }
        });
    });
  }

  loadContacDataOnboarding(user): Promise<any> {
    return new Promise((resolve, reject) => {
      forkJoin([
        this.addressHelper.getStreetTypes(),
        this.addressHelper.getProvincesList(),
      ]).pipe(spinner(RegisterLoadingId.LOAD_CONTACT_DATA))
        .subscribe({
          next: res => {
            const userAux = { ...user, streetTypes: res[0], provinces: res[1] };
            resolve(userAux);
          }
        });
    });

  }

  startRegisterOnlineCampaign(data) {
    this.resetState();
    this.registerService.getOnelineCampaignUserData(data)
      .subscribe(response => {
        const action = this.validateOnelineCampaignResponseHandler(response, data.documentNumber);
        action();
      });
  }

  getPageTitle(title: string): string {
    return this.analyticsContext.getPageTitle(title);
  }

  async navigateToTeCuidamos(state: Partial<RegisterState>) {
    await this.ui.presentLoading('teCuidamos-sso');
    const link: any = await this.registerService.getSsoUrl('tecuidamos-button', state).toPromise().catch(() => null as string);
    this.ui.dismissLoading('teCuidamos-sso');
    if (link) {
      this.iab.create(link, '_self');
      this.nav.goHomePage();
    } else {
      this.nav.toNew('/tecuidamos/tc-cant-register');
    }
  }

  getDeviceStructure(code: string, state?: Partial<RegisterState>, error?: HttpErrorResponse) {
    const structure: {
      structure?: string;
      versionApp?: string;
      versionOS?: string;
      deviceModel?: string;
      userAgent?: string;
      operatingSystem?: string;
      onlineRegistration?: boolean;
      error?: HttpErrorResponse;
    } = { structure: code };
    structure.versionApp = Environment.config().version;

    if (this.platform.is('cordova')) {
      structure.versionOS = this.device.version;
      structure.deviceModel = [this.device.manufacturer, this.device.model].join(' ');
    } else {
      structure.userAgent = getUserAgent();
      structure.operatingSystem = navigator.platform;
    }

    if (state) {
      structure.onlineRegistration = !state.onlineCampaign;
    }

    if (error) {
      structure.error = error;
    }

    return structure;
  }

  private getDefaultAnalyticsErrorParams(title: string, code: string, message: string) {
    return {
      Pagetitle: `${METS_PREFIX_REGISTER_PAGE_TITLE}${title}`,
      CD115_error_type: code,
      CD116_error_message: message,
      CD117_error_location: this.ctx.currentPage,
      CD133_process_step_number: 'step-error',
      ...getRegisterCommonLiterals()
    };
  }

  private customerSupport() {
    this.nav.toNew('support');
  }

  private mapUserData(
    user: Partial<RegisterUserHttpResponse>,
    documentNumber?: string
  ): Partial<RegisterState> {
    const address = user.address || {};
    let door = address?.door;
    if (door) {
      door = hasSpecialCharacters(address?.door) ? null : address.door;
    }

    return {
      campaignDesc: user.campaignDesc,
      name: user.name,
      lastname1: user.surname1,
      lastname2: user.surname2,
      birthdate: new Date(moment(user.birthDate, "DD-MM-YYYY")?.toDate()),
      emailRes: user.email,
      documentType: getDocumentNumberType(documentNumber || user.documentNumber || ''),
      documentNumber: documentNumber?.toUpperCase() || user.documentNumber?.toUpperCase(),
      phoneRes: user.mobile,
      teCuidamos: user.teCuidamos === 'S',
      gender: user.gender === '2' ? 'FEMALE' : 'MALE',
      locality: address?.city,
      door,
      floor: address?.floor,
      streetNumber: address?.number,
      doorway: address?.portal,
      postalCode: address?.postalCode,
      province: address?.province,
      street: address?.street,
      streetType: address?.streetType,
      token: user.token,
      tokenValidation: user.token,
      hasHealthPolicies: user.hasHealthPolicies
    };
  }

  private mapUserDataHashTC(
    user: Partial<RegisterUserHttpResponse>,
    documentNumber?: string
  ): Partial<RegisterState> {

    return {
      name: user.name,
      lastname1: user.surname1,
      lastname2: user.surname2,
      email: user.email,
      documentType: getDocumentNumberType(documentNumber || user.documentNumber || ''),
      documentNumber: documentNumber?.toUpperCase() || user.documentNumber?.toUpperCase(),
      phone: user.mobilePhone ? user.mobilePhone : user.mobile,
    };
  }

  private validateOnelineCampaignResponseHandler(res, documentNumber) {
    const data = this.mapUserData(res, documentNumber);
    const code = res.code;
    const defaultAction = () => this.setIncorrectLoginTemporal(true);
    const actions = {
      [ValidateOnlineCampaignUserResponseCodes.OK]: () => {
        this.resetState();
        const srv = this.nav.getQueryParams('srv');
        this.updateState({
          ...data,
          documentNumber,
          onlineCampaign: true,
          teCuidamos: res.teCuidamos === 'S',
          srv
        });
        const queryParams = srv ? { srv } : null;
        this.setIncorrectLoginTemporal(false);
        this.attemptsRegister = 3;
        this.navTo(RegisterRouting.RGPDUE, queryParams);
      },
      [ValidateOnlineCampaignUserResponseCodes.UNIDENTIFIED_CUSTOMER]: () => {
        this.setIncorrectLoginTemporal(true);
      }
    };

    const action = actions[code];
    return !action
      ? defaultAction
      : (typeof action === 'function')
        ? action
        : defaultAction;
  }

  private setErrorInEmailInputs(form: FormGroup, passwordDataForm: boolean): void {
    const error = { 'validEmail': true };

    if (passwordDataForm) {
      const emailControl = form.get('passwordDataForm').get('email');
      const confirmEmailControl = form.get('passwordDataForm').get('confirmEmail');
      emailControl.setErrors(error);
      emailControl.markAllAsTouched();
      confirmEmailControl.setErrors(error);
      confirmEmailControl.markAllAsTouched();
      return;
    }

    setTimeout(() => {
      form.get('email').setErrors(error);
      form.get('email').markAsTouched();
    });
  }

  private navToResultRegister(paramsUrl = false): void {
    this.navTo(paramsUrl ? RegisterRouting.RESULT + '/' + Date.now() : RegisterRouting.RESULT);
  }

  private getResultCustomHeaderOptions(): Partial<ResultCustomHeader> {
    return {
      title: {
        text: 'REGISTER.HEADER.TITLE'
      }
    };
  }

  private getResultCustomButtonOptions(key: 'openPhone', path: string): Partial<ResultCustomButtons> {
    const phone = this.getPhoneNumber();
    const phoneText = this.translate.instant(`${path}PRIMARY_BTN`, { phone });
    const buttons = {
      openPhone: {
        primary: {
          text: phoneText,
          show: this.ctx.isApp,
          icon: 'call',
          onClick: () => {
            if (phone) this.url.openPhone(phone);
          }
        },
        secondary: {
          show: true,
          text: `${path}SECONDARY_BTN`,
          onClick: () => this.nav.cancelProcess()
        }
      }
    };

    return buttons[key];
  }

  onPostalCodeChange(form: FormGroup, provinces: Province[]): void {
    if (!form.get('postalCode').value) {
      form.get('province').setValue(null);
    }
    if (!form.get('postalCode').valid) {
      form.get('street').setValue(null);
      form.get('locality').setValue(null);
      form.get('streetNumber').setValue(null);
    }
    if (form.get('postalCode')?.value?.length >= 2) {
      const province = this.addressHelper.findProvinceByPostalCode(
        form.get('postalCode').value,
        provinces
      );
      if (province) {
        form.get('province').setValue(province.name);
      }
    }
  }

  forwardedPinBySMS(state: Partial<RegisterState>) {
    return this.forwardedPinCodeBySMS(state);
  }

  forwardedPinByEmail(state: Partial<RegisterState>, emailVerification) {
    return this.forwardedPinCodeByEmail(state, emailVerification);
  }

  openSendPinSMSFewMinutesModal(): void {
    const componentProps = {
      maxWidth: '100%',
      panelClass: 'custom-dialog-container',
      data: {
        values: [{
          icon: 'image-header afin-icon afin-icon-alert-ko',
          iconSrc: 'assets/images/icon-alert-ko.svg',
          type: 'p',
          value: this.translate.instant('REGISTER.ERRORS.SEND_SMS_PIN_302.MESSAGE'),
          class: 'font-size16 dmsans-r mapfre-mt-s'
        }],
        actions: {
          class: 'horizontal',
          buttons: [
            {
            text: this.translate.instant('UNDERSTOOD'),
            handler: () => {},
            }
          ]
        },
        ctx: this.ctx
      }
    };
    this.ui.openComponentAsModal(
      ErrorSendSMSModalComponent,
      componentProps,
      'error-send-sms-modal'
    );
  }

  private onResetLocation(form: FormGroup): void {
    if (!form.get('locality').value) {
      form.get('street').setValue(null);
      form.get('streetNumber').setValue(null);
    }
  }

  private navToSuccessRegisterUserMTCError(s: Partial<RegisterState>, loginFn: Function): void {
    const state = s;
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: 'REGISTER.RESULT_SUCCESS_REGISTER.TITLE_NO_MTC',
        message: 'REGISTER.RESULT_SUCCESS_REGISTER.MESSAGE_NO_MTC',
        infoCall: false,
        errMtC: 'REGISTER.RESULT_SUCCESS_REGISTER.ERROR_MTC',
        imgDesktop: 'assets/images/register-success-desktop.png',
        imgMobile: 'assets/images/register-success-mobile.png',
      },
      buttons: {
        primary: {
          type: 'primary',
          text: 'REGISTER.RESULT_SUCCESS_REGISTER.BUTTON_PRIMARY_NO_MTC',
          onClick: () => loginFn(state)
        },
        reverse: true
      }
    };

    if (this.ctx?.isDesktop) {
      options.buttons.buttonsDownloadApp = true;
    }

    this.resultRegisterHelper.options = options;
    this.resultRegisterHelper.analytics = {
      Pagetitle: this.getPageTitle(RegisterAnalyticsPageTitles.REGISTER_SUCCESS_TECUIDAMOS_ERROR),
      CD133_process_step_number: 'step-8'
    };
    this.navToResultRegister();
  }

  private navToChangeUserStatusError(email: string, code: string, message: string): void {
    const path = 'REGISTER.ERRORS.CHANGE_USER_STATUS.';
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: this.translate.instant(`${path}TITLE`, { email }),
        message: `${path}MESSAGE`,
        infoCall: false
      },
      buttons: {
        primary: {
          type: 'primary',
          text: `${path}PRIMARY_BTN`,
          onClick: () => this.nav.cancelProcess()
        }
      }
    };
    if (this.isSupportFormActive) {
      options.buttons.secondary = {
        text: `${path}SECONDARY_BTN`,
        type: 'link',
        onClick: () => this.customerSupport()
      };
    }

    this.resultRegisterHelper.options = options;
    this.resultRegisterHelper.analytics = this.getDefaultAnalyticsErrorParams(RegisterAnalyticsPageTitles.CHANGE_USER_STATUS, code, message);
    this.navToResultRegister();
  }


  private navToRegisterError(s: Partial<RegisterState>, code: string, message: string): void {
    const path = 'REGISTER.ERRORS.REGISTER_ERROR.';
    const state = s;
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: `${path}TITLE`,
        message: `${path}MESSAGE`,
        infoCall: false
      },
      buttons: {
        primary: {
          type: 'primary',
          text: `${path}PRIMARY_BTN`,
          onClick: () => (state.onlineCampaign) ? this.nav.cancelProcess() : this.navTo(RegisterRouting.INITIAL_DATA)
        }
      }
    };
    if (this.isSupportFormActive) {
      options.buttons.secondary = {
        text: `${path}SECONDARY_BTN`,
        type: 'link',
        onClick: () => this.customerSupport()
      };
    }

    this.resultRegisterHelper.options = options;
    this.resultRegisterHelper.analytics = this.getDefaultAnalyticsErrorParams(RegisterAnalyticsPageTitles.REGISTER_ERROR, code, message);
    this.navToResultRegister();
  }


  private navToUserCancelledError(code: string, message: string): void {
    const path = 'REGISTER.ERRORS.ALREADY_REGISTER.';
    const options: Partial<ResultRegisterOptions> = {
      data: {
        title: `REGISTER.ERRORS.USER_CANCELLED_TITLE`,
        message: `${path}MESSAGE`,
        infoCall: false
      },
      buttons: {
        primary: {
          type: 'primary',
          text: `${path}PRIMARY_BTN`,
          onClick: () => this.nav.cancelProcess()
        }
      }
    };
    if (this.isSupportFormActive) {
      options.buttons.secondary = {
        text: `${path}SECONDARY_BTN`,
        type: 'link',
        onClick: () => this.customerSupport()
      };
    }

    this.resultRegisterHelper.options = options;
    this.resultRegisterHelper.analytics = this.getDefaultAnalyticsErrorParams(RegisterAnalyticsPageTitles.REGISTER_ERROR, code, message);
    this.navToResultRegister();
  }

  private sendPinCodeBySMS(state: Partial<RegisterState>, navPop = false) {
    this.registerService.getPinSMS({
      phoneNumber: state.phone,
      documentNumber: state.documentNumber
    }).subscribe(res => {
      const structure = JSON.stringify(this.getDeviceStructure(RegistersStructure.SMS_SUCCESS, state));
      this.or.customerSignUpRO(
        RegistersRO.SMS_SUCCESS,
        structure,
        state
      );
      if (navPop) {
        this.nav.pop();
      }
      this.navTo(RegisterRouting.SECURITY_PHONE_CODE);
    }, (error) => {
      const structure = JSON.stringify(this.getDeviceStructure(RegistersStructure.SMS_ERROR, state, error));
      this.or.customerSignUpRO(
        RegistersRO.SMS_ERROR,
        structure,
        state
      );
      if (error?.error?.code === RegisterErrors.WAIT_FEW_MINUTOS_PIN_SMS) {
        this.openSendPinSMSFewMinutesModal();
        return;
      }
      const forwardedPinBySMS = () => this.sendPinCodeBySMS(state, true);
      this.navSendSMSPinError(forwardedPinBySMS);
    });
  }

  private forwardedPinCodeBySMS(state: Partial<RegisterState>) {
    return this.registerService.getPinSMS({
      phoneNumber: state.phone,
      documentNumber: state.documentNumber
    });
  }

  private forwardedPinCodeByEmail(state: Partial<RegisterState>, emailVerification: boolean) {
    return this.registerService.getPinEmail(state, emailVerification);
  }

  onSendPinCodeByEmail(state: Partial<RegisterState>, emailVerification: boolean) {
    this.sendPinCodeByEmail(state, emailVerification);
  }

  private sendPinCodeByEmail(state: Partial<RegisterState>, emailVerification = false) {
    this.registerService.getPinEmail(
      state,
      emailVerification
    ).subscribe(res => {
      const queryParams = { register: emailVerification };
      this.navTo(RegisterRouting.SECURITY_EMAIL_CODE, queryParams);
    }, () => {
      this.navSendEmailPinError();
    });
  }

  private getPhoneNumber(): string {
    return this.mmobileHelper.getCustomConfigData(
      PHONE_NUMBERS,
      'registerPhone'
    ) || null;
  }

  private sendRegisterRequest(state, login) {
    if (!this.checkBirthdate(state.hasHealthPolicies, moment(state.birthdate, 'DD-MM-YYYY').format('YYYY-MM-DD'))) {
      this.navToCustomerNoProfileError();
      return;
    }
    this.registerService.sendRegisterRequest(state).subscribe(res => {
      const action = this.registerUserResponseHandler(
        res,
        state,
        login.bind(this));
      action();
    }, () => this.navToError());
  }

  private goToPortalHome() {
    this.urlExternal.openExternalUrl('https://www.mapfre.es', null, null, { web: { sameTab: true } });
  }
}
